import * as d3 from "d3";
import React, { useEffect, useRef } from "react";
import { EnrichedPriceRow, effectiveClose } from "./types";

interface PriceChartProps {
  rows: EnrichedPriceRow[];
  isDarkMode: boolean;
  height?: number;
}

const MARGINS = { top: 12, right: 48, bottom: 28, left: 8 };

const PriceChart: React.FC<PriceChartProps> = ({
  rows,
  isDarkMode,
  height = 160,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || rows.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const totalWidth = containerRef.current.offsetWidth;
    const w = totalWidth - MARGINS.left - MARGINS.right;
    const h = height - MARGINS.top - MARGINS.bottom;
    if (w <= 0 || h <= 0) return;

    svg
      .attr("width", totalWidth)
      .attr("height", height)
      .attr("viewBox", `0 0 ${totalWidth} ${height}`);

    const g = svg
      .append("g")
      .attr("transform", `translate(${MARGINS.left},${MARGINS.top})`);

    const gridColor = isDarkMode ? "#333" : "#E5E7EB";
    const textColor = isDarkMode ? "#9CA3AF" : "#6B7280";

    const sorted = [...rows].sort(
      (a, b) =>
        new Date(a.datetimeUtc).getTime() - new Date(b.datetimeUtc).getTime()
    );

    const dates = sorted.map((r) => new Date(r.datetimeUtc));
    const effCloses = sorted.map((r) => effectiveClose(r));
    const validCloses = effCloses.filter((v) => !Number.isNaN(v));
    if (validCloses.length === 0) return;

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(dates) as [Date, Date])
      .range([0, w]);

    const yMin = Math.min(...validCloses);
    const yMax = Math.max(...validCloses);
    const yPad = yMax === yMin ? Math.abs(yMax) * 0.1 || 1 : (yMax - yMin) * 0.1;
    const yScale = d3
      .scaleLinear()
      .domain([yMin - yPad, yMax + yPad])
      .range([h, 0])
      .nice();

    // Grid
    const yTicks = yScale.ticks(4);
    g.selectAll("line.hg")
      .data(yTicks)
      .enter()
      .append("line")
      .attr("x1", 0)
      .attr("x2", w)
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d))
      .attr("stroke", gridColor)
      .attr("stroke-dasharray", "3 3")
      .attr("opacity", 0.4);

    // Failed zone shading
    let failStart: number | null = null;
    sorted.forEach((r, i) => {
      if (r.isFailed && failStart === null) {
        failStart = i;
      }
      if ((!r.isFailed || i === sorted.length - 1) && failStart !== null) {
        const endIdx = r.isFailed ? i : i - 1;
        const x1 = xScale(dates[failStart]);
        const x2 = xScale(dates[endIdx]);
        const pad = (x2 - x1) * 0.05 || 4;
        g.append("rect")
          .attr("x", x1 - pad)
          .attr("y", 0)
          .attr("width", x2 - x1 + pad * 2)
          .attr("height", h)
          .attr("fill", isDarkMode ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.06)")
          .attr("rx", 2);
        failStart = null;
      }
    });

    // Line using effective close (reflects pending overrides in realtime)
    const line = d3
      .line<(typeof sorted)[0]>()
      .x((_d, i) => xScale(dates[i]))
      .y((d) => {
        const v = effectiveClose(d);
        return Number.isNaN(v) ? h : yScale(v);
      })
      .defined((d) => {
        const hasPending = !!d.pendingValue;
        const hasOverride = d.isOverridden;
        if (hasPending || hasOverride) return true;
        return !Number.isNaN(Number(d.close)) && !d.isFailed;
      })
      .curve(d3.curveLinear);

    g.append("path")
      .datum(sorted)
      .attr("fill", "none")
      .attr("stroke", isDarkMode ? "#9CA3AF" : "#6B7280")
      .attr("stroke-width", 1.5)
      .attr("d", line);

    // Saved override dots
    const overridden = sorted.filter((r) => r.isOverridden && !r.pendingValue);
    g.selectAll("circle.ov")
      .data(overridden)
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(new Date(d.datetimeUtc)))
      .attr("cy", (d) => yScale(effectiveClose(d)))
      .attr("r", 3)
      .attr("fill", "#CC7A00");

    // Pending override dots (green, pulsing)
    const pending = sorted.filter((r) => !!r.pendingValue);
    g.selectAll("circle.pend")
      .data(pending)
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(new Date(d.datetimeUtc)))
      .attr("cy", (d) => yScale(effectiveClose(d)))
      .attr("r", 4)
      .attr("fill", "#22C55E")
      .attr("opacity", 0.9);

    // Axes
    const xTicks = Math.max(3, Math.floor(w / 80));
    g.append("g")
      .attr("transform", `translate(0,${h})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(xTicks)
          .tickSize(0)
          .tickPadding(4)
          .tickFormat((d) => d3.timeFormat("%b %d %H:%M")(d as Date))
      )
      .call((sel) => sel.select(".domain").attr("stroke", gridColor))
      .selectAll("text")
      .attr("fill", textColor)
      .style("font-size", "9px");

    g.append("g")
      .attr("transform", `translate(${w},0)`)
      .call(
        d3
          .axisRight(yScale)
          .ticks(4)
          .tickSize(0)
          .tickPadding(4)
          .tickFormat((d) => String(d))
      )
      .call((sel) => sel.select(".domain").attr("stroke", gridColor))
      .selectAll("text")
      .attr("fill", textColor)
      .style("font-size", "10px");
  }, [rows, isDarkMode, height]);

  // Resize
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      svgRef.current?.dispatchEvent(new Event("resize"));
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-gray-400 dark:text-gray-600">
        No price data in range
      </div>
    );
  }

  return (
    <div ref={containerRef} data-testid="price-chart" className="w-full overflow-hidden">
      <svg ref={svgRef} className="w-full" />
    </div>
  );
};

export default PriceChart;
